import { useEffect, useRef, useState } from "react";
import {
  AppAvatar,
  AppBox,
  AppButton,
  AppGeneralSearch,
  AppIconButton,
  AppLink,
  AppText,
  AppTitle,
} from "../../../../../commons/components";
import AppBorderBox from "../../../../../commons/components/BorderBox";
import { ManageFootballPlayer } from "../../../../../commons/models/football/admin/manage-football-player";
import { toSentenceCase, toTitleCase } from "../../../../../commons/utilities/string-utils";
import { useAppDispatch, useAppSelector } from "../../../../../hooks/app";
import { PlayerStatusEnum } from "../../../../../commons/enums/player-status-enum";
import IconMultiselect from "../../../../../commons/components/icons/multiselect";
import {
  cleanUpGeneralSearchTitle,
  setGeneralCleanSearchField,
} from "../../../../../redux/slices/general-search-slice";
import { IFootballSubTeamDetail } from "../../../../../commons/models/football/interface/i-football-sub-team";
import {
  cleanUpSubTeamPlayersAvailable,
  fetchManageSubTeamPlayersAvailable,
  ISubTeamPlayersAvailableOptions,
} from "../../../../../redux/slices/football/admin/team/sub-team/squad/manage-sub-team-players-available-slice";

interface PlayerSearchForSubTeamProps {
  onSelectedPlayer?: (players: ManageFootballPlayer[]) => void;
  withMultiSelect?: boolean;
  subTeam: IFootballSubTeamDetail;
  userSelectedPlayers?: ManageFootballPlayer[];
}
export default function PlayerSearchForSubTeam({
  onSelectedPlayer,
  subTeam,
  withMultiSelect,
  userSelectedPlayers,
}: PlayerSearchForSubTeamProps) {
  const dispatch = useAppDispatch();
  const multiSelectRef = useRef<HTMLInputElement>(null);
  const [isMultiSelect, setMultiSelect] = useState(false);
  const [isUncheckAll, setUncheckAll] = useState(false);
  const [selectedPlayers, setSelectedPlayers] = useState<ManageFootballPlayer[]>([]);
  const [filteredPlayers, setFilteredPlayers] = useState<ManageFootballPlayer[]>([]);
  const { subTeamPlayersAvailable } = useAppSelector((state) => state.footballManageSubTeamPlayersAvailable);
  const { generalSearchTitle } = useAppSelector((state) => state.generalSearch);

  useEffect(() => {
    dispatch(setGeneralCleanSearchField(true));
    dispatch(cleanUpGeneralSearchTitle());
  }, []);

  useEffect(() => {
    const playerAvailableOption: ISubTeamPlayersAvailableOptions = {
      teamId: subTeam.parent.id,
      subTeamId: subTeam.id,
      urlOption: {
        sort: "firstName",
        name: generalSearchTitle,
        status: PlayerStatusEnum.Active,
        page: 0,
      },
    };
    dispatch(cleanUpSubTeamPlayersAvailable());
    dispatch(fetchManageSubTeamPlayersAvailable(playerAvailableOption));
  }, [generalSearchTitle]);

  useEffect(() => {
    const selectedIds = new Set();
    if (userSelectedPlayers && userSelectedPlayers.length > 0) {
      userSelectedPlayers.forEach((player) => selectedIds.add(player.id));
    }
    if (subTeamPlayersAvailable.length > 0) {
      let filteredPlayers = [];
      if (selectedIds) {
        filteredPlayers = subTeamPlayersAvailable.all().filter((player) => !selectedIds.has(player.id));
      } else {
        filteredPlayers = subTeamPlayersAvailable.all();
      }
      setFilteredPlayers(filteredPlayers);
    } else {
      setFilteredPlayers([]);
    }
  }, [subTeamPlayersAvailable, userSelectedPlayers]);

  useEffect(() => {
    if (isUncheckAll) {
      setSelectedPlayers([]);
      setUncheckAll(false);
    }
  }, [isUncheckAll]);

  const handlePlayerClick = (event: Event, player: ManageFootballPlayer) => {
    event.preventDefault();
    if (onSelectedPlayer) {
      onSelectedPlayer([player]);
    }
  };

  const handleMultiSelectClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    multiSelectRef.current?.click();
  };

  const handleTeamSelection = (e: any, player: ManageFootballPlayer) => {
    if (e.target.checked) {
      setSelectedPlayers([...selectedPlayers, player]);
    } else {
      setSelectedPlayers(selectedPlayers.filter((t) => t.id !== player.id));
    }
  };

  const handleSavePlayers = () => {
    if (onSelectedPlayer) {
      onSelectedPlayer(selectedPlayers);
    }
  };

  return (
    <AppBox flexDirection="column" gap="md">
      <AppBox flexDirection="column" gap="sm">
        <AppBox gap="xs">
          <AppBox className="w-100 flex-1">
            <AppGeneralSearch />
          </AppBox>
          {withMultiSelect && (
            <>
              <AppIconButton
                icon={<IconMultiselect />}
                variant="light"
                onClick={(e) => handleMultiSelectClick(e)}
                color={isMultiSelect ? "primary" : "gray"}
                borderLight
              />
              <input
                type="checkbox"
                hidden
                ref={multiSelectRef}
                checked={isMultiSelect}
                onChange={(e) => {
                  setMultiSelect(e.target.checked);
                  setSelectedPlayers([]);
                }}
              />
            </>
          )}
        </AppBox>
        <AppBox flexDirection="column" style={{ height: "30rem", overflowY: "scroll" }}>
          {filteredPlayers &&
            filteredPlayers.map((player, index) => (
              <AppBorderBox border={["Bottom"]} py="xs" key={index}>
                {isMultiSelect ? (
                  <AppBox justifyContent="space-between" alignItems="center" pr="xs" className="flex-1">
                    <AppBox justifyContent="space-between" alignItems="center" className="flex-1">
                      <AppBox gap="xs" alignItems="center">
                        <AppAvatar username={player.fullName || ""} src={player?.avatar} size="md" />
                        <AppBox flexDirection="column">
                          <AppTitle as="h6">{toTitleCase(player?.displayName)}</AppTitle>
                          {player.position && (
                            <AppText as="span" fontWeight="semibold" size="sm">
                              {toSentenceCase(player?.position)}
                            </AppText>
                          )}
                        </AppBox>
                      </AppBox>
                    </AppBox>
                    <label className="ee-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedPlayers.some((p) => p.id === player.id)}
                        onChange={(e) => {
                          handleTeamSelection(e, player);
                        }}
                      />
                      <span className="checkmark"></span>
                    </label>
                  </AppBox>
                ) : (
                  <AppLink onClick={(e) => handlePlayerClick(e, player)}>
                    <AppBox justifyContent="space-between" alignItems="center" className="flex-1">
                      <AppBox gap="xs" alignItems="center">
                        <AppAvatar username={player.fullName || ""} src={player?.avatar} size="md" />
                        <AppBox flexDirection="column">
                          <AppTitle as="h6">{toTitleCase(player?.displayName)}</AppTitle>
                          {player.position && (
                            <AppText as="span" fontWeight="semibold" size="sm">
                              {toSentenceCase(player?.position)}
                            </AppText>
                          )}
                        </AppBox>
                      </AppBox>
                    </AppBox>
                  </AppLink>
                )}
              </AppBorderBox>
            ))}
          {filteredPlayers.length === 0 && <AppText>There are not any players</AppText>}
        </AppBox>
      </AppBox>
      {isMultiSelect && (
        <AppBox justifyContent="end" gap="xs">
          <AppButton variant="outline" borderLight label="Clear" color="gray" onClick={() => setUncheckAll(true)} />
          <AppButton label={"Add Players"} onClick={handleSavePlayers} />
        </AppBox>
      )}
    </AppBox>
  );
}
